import { useAtomRefresh, useAtomSet, useAtomValue } from "@effect/atom-react";
import { queryAtomEffect } from "@crm/shared/query-atom/effect";
import { queryAtomPaged } from "@crm/shared/query-atom/paged";
import { rpcEffectCaller } from "@crm/rpc-client";
import {
  KnowledgeEntriesRpc,
  KnowledgeEntryCoursesRpc,
  KnowledgeEntryTagsRpc,
  KnowledgeTagsRpc,
} from "@crm/rpc-contract/groups/knowledge";
import { Cause, Option } from "effect";
import * as AsyncResult from "effect/unstable/reactivity/AsyncResult";
import { useCallback, useMemo } from "react";

/**
 * 知识库查询域（Effect-native）。
 *
 * - 查询：queryAtomEffect + rpcEffectCaller（typed error 保留在 AsyncResult）
 * - 同屏失效：mutation 的 reactivityKeys → 订阅中的查询自动重跑
 * - 跨页兜底：staleTime 控制挂载即后台重验
 */

// 类型化 Effect RPC 调用器：所有查询保留错误通道。
const knowledgeEntriesFx = rpcEffectCaller(KnowledgeEntriesRpc);
const knowledgeTagsFx = rpcEffectCaller(KnowledgeTagsRpc);
const knowledgeEntryTagsFx = rpcEffectCaller(KnowledgeEntryTagsRpc);
const knowledgeEntryCoursesFx = rpcEffectCaller(KnowledgeEntryCoursesRpc);

// =============================================================================
// Knowledge Entries Queries
// =============================================================================

const knowledgeEntryStatsAtom = queryAtomEffect(
  () => knowledgeEntriesFx.getKnowledgeEntryStats(undefined),
  { staleTime: 5 * 60 * 1000, reactivityKeys: ["knowledgeEntry"] },
);

/**
 * Hook: Get knowledge entry statistics (Optimized)
 *
 * 返回各状态的条目数量，用于首页展示。
 * 数据量极小（~100 bytes），性能优化替代全量加载。
 */
export function useKnowledgeEntryStats() {
  return useAtomValue(knowledgeEntryStatsAtom([]));
}

type KnowledgeEntryStatus = "pending_review" | "approved" | "draft" | "published";

type KnowledgeEntriesInfiniteOptions = {
  status: KnowledgeEntryStatus;
  search?: string;
  tagId?: string;
  courseId?: string;
  pageSize?: number;
};

type KnowledgeEntriesInfiniteKey = readonly [
  KnowledgeEntryStatus,
  string | undefined,
  string | undefined,
  string | undefined,
  number,
];

const knowledgeEntriesInfiniteAtom = queryAtomPaged(
  (key: KnowledgeEntriesInfiniteKey, page: number) =>
    knowledgeEntriesFx.getKnowledgeEntriesPaginated({
      status: key[0],
      page,
      pageSize: key[4],
      search: key[1],
      tagId: key[2],
      courseId: key[3],
    }),
  { staleTime: 5 * 60 * 1000, reactivityKeys: ["knowledgeEntry"] },
);

/**
 * Hook: Get paginated knowledge entries (infinite scroll)
 *
 * 基于 queryAtomPaged（Atom.pull + Stream.paginate）的真分页：
 * - 挂载加载第一页；fetchNextPage 写入 pull atom → 拉下一页并累积
 * - hasNextPage/totalCount 来自最后已加载页（服务端真值，非恒 false）
 * - mutation 的 reactivityKeys ["knowledgeEntry"] 失效 → 丢弃累积、回第一页
 *   重拉（reactivityKeys 语义与 queryAtom 一致）
 * 不含 content 字段，大幅减少传输量。
 */
export function useKnowledgeEntriesInfinite({
  status,
  search,
  tagId,
  courseId,
  pageSize = 20,
}: KnowledgeEntriesInfiniteOptions) {
  const atom = knowledgeEntriesInfiniteAtom([status, search, tagId, courseId, pageSize]);
  const result = useAtomValue(atom);
  const fetchNext = useAtomSet(atom);
  const refresh = useAtomRefresh(atom);

  const state = AsyncResult.value(result).pipe(Option.getOrUndefined);
  const items = useMemo(() => state?.items ?? [], [state]);

  // 已到末页时 no-op（拉流已 done，但避免无谓的 waiting 态切换）
  const hasNextPage = state?.hasNextPage ?? false;
  const fetchNextPage = useCallback(() => {
    if (hasNextPage) {
      fetchNext(undefined);
    }
  }, [fetchNext, hasNextPage]);

  return {
    items,
    totalCount: state?.totalCount ?? 0,
    isLoading: AsyncResult.isInitial(result),
    isFetching: AsyncResult.isWaiting(result),
    isFetchingNextPage: AsyncResult.isWaiting(result) && items.length > 0,
    hasNextPage,
    fetchNextPage,
    refetch: refresh,
    error: AsyncResult.isFailure(result) ? Cause.squash(result.cause) : null,
  };
}

const knowledgeEntryDetailAtom = queryAtomEffect(
  (key: readonly [string]) => knowledgeEntriesFx.getKnowledgeEntryById(key[0]),
  {
    staleTime: 5 * 60 * 1000,
    reactivityKeys: (key) => ["knowledgeEntry", `knowledgeEntry:${String(key[0])}`],
  },
);

/**
 * Hook: Get single knowledge entry by ID
 *
 * 获取单个知识条目，会增加阅读计数（仅 published）
 *
 * @param id 条目 ID
 */
export function useKnowledgeEntry(id: string) {
  const atom = knowledgeEntryDetailAtom([id], !!id);
  const result = useAtomValue(atom);
  const refresh = useAtomRefresh(atom);
  return {
    result,
    refresh,
    isFailure: AsyncResult.isFailure(result),
    error: AsyncResult.isFailure(result) ? Cause.squash(result.cause) : null,
  };
}

// =============================================================================
// Knowledge Tags Queries
// =============================================================================

const knowledgeTagsAtom = queryAtomEffect(() => knowledgeTagsFx.getKnowledgeTags(undefined), {
  staleTime: 5 * 60 * 1000,
  reactivityKeys: ["knowledgeTag"],
});

/**
 * Hook: Get all knowledge tags
 *
 * 返回所有知识标签，按排序顺序排列。
 */
export function useKnowledgeTags() {
  return useAtomValue(knowledgeTagsAtom([]));
}

// =============================================================================
// Knowledge Entry Relations Queries
// =============================================================================

const knowledgeEntryTagsAtom = queryAtomEffect(
  () => knowledgeEntryTagsFx.getKnowledgeEntryTags(undefined),
  { staleTime: 5 * 60 * 1000, reactivityKeys: ["knowledgeEntryTag"] },
);

/**
 * Hook: Get all entry-tag relationships
 *
 * 返回知识条目与标签的关联关系。
 * Permission: knowledge-tag:read:all
 */
export function useKnowledgeEntryTags() {
  return useAtomValue(knowledgeEntryTagsAtom([]));
}

const knowledgeEntryCoursesAtom = queryAtomEffect(
  () => knowledgeEntryCoursesFx.getKnowledgeEntryCourses(undefined),
  { staleTime: 5 * 60 * 1000, reactivityKeys: ["knowledgeEntryCourse"] },
);

/**
 * Hook: Get all entry-course relationships
 *
 * 返回知识条目与课程的关联关系。
 * - 非学员：返回所有关联
 * - 学员：只返回已报名课程的关联
 */
export function useKnowledgeEntryCourses() {
  return useAtomValue(knowledgeEntryCoursesAtom([]));
}
